import {
  Component,
  inject,
  Input,
  OnInit,
  signal,
  computed,
  ViewChild,
  ElementRef,
  OnDestroy,
} from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { toSignal } from '@angular/core/rxjs-interop';
import { startWith } from 'rxjs';
import { BaseAuthComponent } from '../../common/BaseAuthComponent.class';
import { BusinessService } from '../../../services/business';
import { ToastService } from '../../services';
import { Professional } from '../../interfaces';
import { FieldLabels } from '../../common/formValidHelper';
import flatpickr from 'flatpickr';
import { Spanish } from 'flatpickr/dist/l10n/es.js';
import { Instance as FlatpickrInstance } from 'flatpickr/dist/types/instance';

interface TimePreset {
  label: string;
  start: string;
  end: string;
}

@Component({
  selector: 'app-schedule-block-add',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './schedule-block-add.html',
  styleUrl: './schedule-block-add.css',
})
export class ScheduleBlockAdd extends BaseAuthComponent implements OnInit, OnDestroy {
  @Input() selectedDate?: string | Date | null;
  /** Professional to preselect; null blocks the whole business */
  @Input() selectedProfessionalId?: string | null;
  /** Legacy name of selectedProfessionalId */
  @Input() selectedAgendaId?: string | null;

  private fb = inject(FormBuilder);
  private businessService = inject(BusinessService);
  private toastService = inject(ToastService);

  professionals = signal<Professional[]>([]);
  isLoadingProfessionals = signal(false);
  multiDay = signal(false);

  readonly timePresets: TimePreset[] = [
    { label: 'Todo el día', start: '00:00', end: '23:59' },
    { label: 'Mañana', start: '08:00', end: '12:00' },
    { label: 'Almuerzo', start: '12:00', end: '14:00' },
    { label: 'Tarde', start: '14:00', end: '18:00' },
  ];
  readonly reasons = ['Almuerzo', 'Vacaciones', 'Cita médica', 'Capacitación', 'Festivo'];

  private startDatePicker?: FlatpickrInstance;
  private endDatePicker?: FlatpickrInstance;

  // Pickers attach whenever their input is rendered (the end date only shows for multi-day blocks)
  @ViewChild('startDateInput') set startDateInput(el: ElementRef<HTMLInputElement> | undefined) {
    this.startDatePicker?.destroy();
    this.startDatePicker = el ? this.createPicker(el.nativeElement, 'startDate') : undefined;
  }
  @ViewChild('endDateInput') set endDateInput(el: ElementRef<HTMLInputElement> | undefined) {
    this.endDatePicker?.destroy();
    this.endDatePicker = el ? this.createPicker(el.nativeElement, 'endDate') : undefined;
  }

  blockForm: FormGroup = this.fb.group(
    {
      professionalId: [null as string | null], // null applies to the whole business
      startDate: ['', Validators.required],
      endDate: ['', Validators.required],
      startTime: ['', [Validators.required, Validators.pattern(/^([0-1][0-9]|2[0-3]):[0-5][0-9]$/)]],
      endTime: ['', [Validators.required, Validators.pattern(/^([0-1][0-9]|2[0-3]):[0-5][0-9]$/)]],
      title: ['', Validators.maxLength(200)],
      description: ['', Validators.maxLength(500)],
    },
    { validators: [] }
  );

  private formValue = toSignal(this.blockForm.valueChanges.pipe(startWith(this.blockForm.value)), {
    initialValue: this.blockForm.value,
  });

  /** Plain-language sentence of what will be blocked, shown before saving */
  summary = computed(() => {
    const v = this.formValue();
    if (!v.startDate || !v.startTime || !v.endTime) return null;

    const who = v.professionalId
      ? this.professionalName(this.professionals().find((p) => p.userId === v.professionalId))
      : 'todo el negocio';
    const day = (d: string) =>
      new Intl.DateTimeFormat('es-CO', { weekday: 'long', day: 'numeric', month: 'long' }).format(this.parseLocal(d));
    const dates =
      this.multiDay() && v.endDate && v.endDate !== v.startDate
        ? `del ${day(v.startDate)} al ${day(v.endDate)}`
        : `el ${day(v.startDate)}`;
    const hours =
      v.startTime === '00:00' && v.endTime === '23:59'
        ? 'todo el día'
        : `de ${this.formatTime(v.startTime)} a ${this.formatTime(v.endTime)}`;
    return `Nadie podrá agendar ${dates}, ${hours}, para ${who}.`;
  });

  ngOnInit(): void {
    this.blockForm.addValidators([this.timeRangeValidator, this.dateRangeValidator]);
    this.loadProfessionals();

    const date = this.selectedDate
      ? this.selectedDate instanceof Date
        ? this.formatDate(this.selectedDate)
        : this.selectedDate.slice(0, 10)
      : this.formatDate(new Date());
    this.blockForm.patchValue({
      startDate: date,
      endDate: date,
      professionalId: this.selectedProfessionalId ?? this.selectedAgendaId ?? null,
    });
  }

  ngOnDestroy(): void {
    this.startDatePicker?.destroy();
    this.endDatePicker?.destroy();
  }

  private createPicker(el: HTMLInputElement, control: 'startDate' | 'endDate'): FlatpickrInstance {
    const picker = flatpickr(el, {
      dateFormat: 'Y-m-d',
      altInput: true,
      altFormat: 'l j \\de F',
      locale: Spanish,
      minDate: control === 'endDate' ? this.blockForm.value.startDate : undefined,
      defaultDate: this.blockForm.value[control] || undefined,
      onChange: (dates) => {
        if (!dates.length) return;
        const value = this.formatDate(dates[0]);
        this.blockForm.patchValue({ [control]: value });
        if (control === 'startDate') {
          // Keep the end on or after the start
          const end = this.blockForm.value.endDate;
          if (!this.multiDay() || !end || end < value) {
            this.blockForm.patchValue({ endDate: value });
            this.endDatePicker?.setDate(value, false);
          }
          this.endDatePicker?.set('minDate', value);
        }
      },
    });
    // flatpickr's visible input replaces the original one: keep the label and styles working
    picker.altInput?.classList.add('sb-input');
    picker.altInput?.setAttribute('id', el.id);
    el.removeAttribute('id');
    return picker as FlatpickrInstance;
  }

  setProfessional(id: string | null) {
    this.blockForm.patchValue({ professionalId: id });
  }

  toggleMultiDay() {
    const next = !this.multiDay();
    this.multiDay.set(next);
    if (!next) {
      this.blockForm.patchValue({ endDate: this.blockForm.value.startDate });
    }
  }

  applyPreset(preset: TimePreset) {
    this.blockForm.patchValue({ startTime: preset.start, endTime: preset.end });
    this.blockForm.get('startTime')?.markAsTouched();
    this.blockForm.get('endTime')?.markAsTouched();
  }

  isPresetActive(preset: TimePreset): boolean {
    const v = this.formValue();
    return v.startTime === preset.start && v.endTime === preset.end;
  }

  /** Sets the reason, or clears it when the same chip is pressed again */
  setReason(reason: string) {
    this.blockForm.patchValue({ title: this.blockForm.value.title === reason ? '' : reason });
  }

  professionalName(p?: Professional | null): string {
    if (!p) return 'el profesional';
    const name = `${p.firstName || ''} ${p.lastName || ''}`.trim();
    return name || p.email || 'el profesional';
  }

  showError(name: string): boolean {
    const c = this.blockForm.get(name);
    return !!c && c.invalid && c.touched;
  }

  private loadProfessionals(): void {
    this.isLoadingProfessionals.set(true);
    this.businessService
      .searchEntityByBusinessId(this.businessId, 'professionals', '', 1, 100)
      .subscribe({
        next: (list) => {
          this.professionals.set(list as Professional[]);
          this.isLoadingProfessionals.set(false);
        },
        error: () => this.isLoadingProfessionals.set(false),
      });
  }

  private parseLocal(date: string): Date {
    const [y, m, d] = date.split('-').map(Number);
    return new Date(y, m - 1, d);
  }

  private formatDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private formatTime(time: string): string {
    const [h, m] = time.split(':').map(Number);
    const d = new Date();
    d.setHours(h, m, 0, 0);
    return new Intl.DateTimeFormat('es-CO', { hour: 'numeric', minute: '2-digit' }).format(d);
  }

  private timeRangeValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    const startTime = control.get('startTime')?.value;
    const endTime = control.get('endTime')?.value;
    if (!startTime || !endTime) return null;

    const [sh, sm] = startTime.split(':').map(Number);
    const [eh, em] = endTime.split(':').map(Number);
    return eh * 60 + em <= sh * 60 + sm ? { endTimeBeforeStartTime: true } : null;
  };

  private dateRangeValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    const start = control.get('startDate')?.value;
    const end = control.get('endDate')?.value;
    return start && end && end < start ? { endDateBeforeStartDate: true } : null;
  };

  isValid(): boolean {
    this.blockForm.markAllAsTouched();

    const fieldLabels: FieldLabels = {
      startDate: 'Fecha',
      endDate: 'Hasta',
      startTime: 'Desde',
      endTime: 'Hasta',
      title: 'Motivo',
      description: 'Nota',
    };

    if (this.blockForm.hasError('endTimeBeforeStartTime')) {
      this.toastService.show('La hora final debe ser después de la hora de inicio.', 'warning');
      return false;
    }
    if (this.blockForm.hasError('endDateBeforeStartDate')) {
      this.toastService.show('La fecha final debe ser igual o posterior a la de inicio.', 'warning');
      return false;
    }

    this.toastFormErrors('bloquear el horario', this.blockForm, fieldLabels);
    return this.blockForm.valid;
  }

  getValue() {
    const { professionalId, startDate, endDate, startTime, endTime, title, description } =
      this.blockForm.getRawValue();

    return {
      businessId: this.businessId,
      professionalId: professionalId || null,
      startDate,
      endDate: this.multiDay() ? endDate : startDate,
      startTime,
      endTime,
      title: title?.trim() || null,
      description: description?.trim() || null,
    };
  }
}
